import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { animate } from "motion";
import { addPresentationTestIds } from "../utils/presentationTestIds";

// Progressive enhancement: content stays visible until it enters the viewport.
// No scroll hijacking or permanently hidden content if an animation is interrupted.
export const SiteMotion = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const root = document.getElementById("main-content");
    if (!root) return;
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const seen = new WeakSet();
    const playing = new Map();
    const restore = (element) => { element.style.removeProperty("opacity"); element.style.removeProperty("transform"); };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (!isIntersecting) return;
        observer.unobserve(target);
        if (preference.matches || target.contains(document.activeElement)) return;
        const control = animate(target, { opacity: [0.35, 1], y: [22, 0] }, {
          duration: 0.65, ease: [0.22, 1, 0.36, 1],
          delay: Math.min(Number(target.dataset.revealDelay || 0), 0.24),
          onComplete: () => { restore(target); playing.delete(target); }
        });
        playing.set(target, control);
      });
    }, { threshold: 0.12, rootMargin: "0px 0px -24px 0px" });
    const scan = () => {
      addPresentationTestIds(root, pathname);
      root.querySelectorAll("[data-reveal], h2, .gh-card, .rounded-lg.shadow-lg, .rounded-lg.shadow-md, .rounded-lg.shadow-sm").forEach((element) => {
        if (seen.has(element) || element.closest(".gh-header, footer, form, [data-no-motion]") || element.parentElement?.closest("[data-reveal]")) return;
        seen.add(element);
        element.setAttribute("data-site-reveal", "");
        observer.observe(element);
      });
    };
    const stop = () => {
      playing.forEach((control, element) => { control.cancel(); restore(element); });
      playing.clear();
    };
    const focus = (event) => {
      playing.forEach((control, element) => {
        if (element.contains(event.target)) { control.cancel(); restore(element); playing.delete(element); }
      });
    };
    const mutations = new MutationObserver(scan);
    mutations.observe(root, { childList: true, subtree: true });
    preference.addEventListener("change", stop);
    root.addEventListener("focusin", focus);
    scan();
    return () => {
      observer.disconnect(); mutations.disconnect(); stop();
      preference.removeEventListener("change", stop);
      root.removeEventListener("focusin", focus);
    };
  }, [pathname]);
  return null;
};